// Admin → Comms & integrations → Zoho Sign.
//
// Lets an admin:
//   1. See whether Zoho Sign credentials are configured (status badge).
//   2. Upload a work-order PDF, add one or more recipients, link an account
//      and (optionally) a deal, and hit Send — Zoho Sign emails each signer.
//   3. See every envelope sent through Xentra with live status.
//
// Signed PDFs are stored against the linked account automatically via the
// `/webhooks/zoho-sign` listener (handled server-side).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileUp, PenTool, Send, Trash2, Plus, CheckCircle2, Clock, XCircle, Download, ExternalLink, AlertTriangle } from "lucide-react";

const STATUS_META = {
  sent:             { label: "Out for signature", color: "bg-blue-50 text-blue-700 border-blue-200", Icon: Send },
  viewed:           { label: "Viewed",            color: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: Clock },
  partially_signed: { label: "Partly signed",     color: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  signed:           { label: "Signed",            color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  declined:         { label: "Declined",          color: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
  expired:          { label: "Expired",           color: "bg-slate-100 text-slate-600 border-slate-200", Icon: XCircle },
  recalled:         { label: "Recalled",          color: "bg-slate-100 text-slate-600 border-slate-200", Icon: XCircle },
};

export default function ZohoSignPanel() {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [loadingEnvs, setLoadingEnvs] = useState(true);

  // Compose form state
  const [requestName, setRequestName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [recipients, setRecipients] = useState([{ name: "", email: "" }]);
  const [file, setFile] = useState(null);
  const [sequential, setSequential] = useState(false);
  const [expireDays, setExpireDays] = useState(30);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const reloadStatus = async () => {
    try { const r = await api.get("/zoho-sign/status"); setStatus(r.data); }
    catch (_) { setStatus({ configured: false }); }
  };
  const reloadEnvelopes = async () => {
    setLoadingEnvs(true);
    try { const r = await api.get("/zoho-sign/envelopes"); setEnvelopes(r.data || []); }
    catch (_) { setEnvelopes([]); }
    finally { setLoadingEnvs(false); }
  };

  useEffect(() => {
    reloadStatus();
    reloadEnvelopes();
    (async () => {
      try { const r = await api.get("/accounts"); setAccounts(r.data || []); }
      catch (_) { setAccounts([]); }
    })();
  }, []);

  const canSend = useMemo(() => {
    if (!status?.configured || !file) return false;
    if (!(requestName || "").trim()) return false;
    const ok = recipients.filter((r) => (r.name || "").trim() && (r.email || "").trim().includes("@"));
    return ok.length > 0;
  }, [status, file, requestName, recipients]);

  const addRecipient = () => setRecipients((rs) => [...rs, { name: "", email: "" }]);
  const removeRecipient = (i) => setRecipients((rs) => (rs.length <= 1 ? rs : rs.filter((_, j) => j !== i)));
  const updateRecipient = (i, key, val) => setRecipients((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const resetCompose = () => {
    setRequestName(""); setAccountId(""); setRecipients([{ name: "", email: "" }]);
    setFile(null); setSequential(false); setExpireDays(30);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("body", JSON.stringify({
        request_name:    requestName.trim(),
        recipients:      recipients
          .map((r) => ({ name: r.name.trim(), email: r.email.trim() }))
          .filter((r) => r.name && r.email),
        account_id:      accountId || null,
        expiration_days: Math.max(1, Math.min(90, Number(expireDays) || 30)),
        sequential:      !!sequential,
      }));
      await api.post("/zoho-sign/send", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Work order sent for signature");
      resetCompose();
      reloadEnvelopes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const downloadSigned = async (env) => {
    try {
      const r = await api.get(`/zoho-sign/envelopes/${env.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = env.filename || "signed-work-order.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const configured = status?.configured;

  return (
    <div className="space-y-6" data-testid="zoho-sign-panel">
      {/* ---------- Connection status ---------- */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <PenTool className="h-4 w-4 text-blue-600" /> Zoho Sign (EU)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {configured === undefined ? (
            <div className="text-sm text-slate-500">Checking connection…</div>
          ) : configured ? (
            <div className="flex items-center gap-3 text-sm" data-testid="zoho-sign-status-ok">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <span className="text-emerald-800">Connected to <span className="font-mono">sign.zoho.eu</span>.</span>
              {!status?.webhook_secret_set && (
                <span className="ml-2 text-[11px] font-mono px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700">
                  Webhook secret not set — status callbacks won't update
                </span>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-600 space-y-2" data-testid="zoho-sign-status-missing">
              <div className="flex items-center gap-2 text-rose-700">
                <AlertTriangle className="h-4 w-4" /> <span className="font-medium">Not configured</span>
              </div>
              <ol className="list-decimal pl-5 space-y-1 text-[13px] text-slate-600">
                <li>Open <a href="https://api-console.zoho.eu" target="_blank" rel="noreferrer" className="text-blue-700 underline inline-flex items-center gap-1">Zoho API Console <ExternalLink className="h-3 w-3" /></a> → <b>Add Client ID → Self Client</b>.</li>
                <li>Generate a grant code for scopes <span className="font-mono text-[11px]">ZohoSign.documents.CREATE, READ, UPDATE</span>.</li>
                <li>Exchange it for a refresh token (one-off) and paste the three values into Secrets.</li>
                <li>Create a webhook in <i>Zoho Sign → Settings → Webhooks</i> pointing at <span className="font-mono text-[11px]">{window.location.origin}/webhooks/zoho-sign</span> with HMAC enabled. Paste the generated secret into the webhook secret slot.</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------- Compose ---------- */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <FileUp className="h-4 w-4 text-blue-600" /> Send a work order
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Work-order name *</Label>
              <Input data-testid="zoho-sign-request-name" value={requestName} onChange={(e) => setRequestName(e.target.value)}
                     placeholder="e.g. Acme Industries — PureCyber 24/7 SOC WO" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Linked account</Label>
              <Select value={accountId || "none"} onValueChange={(v) => setAccountId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="zoho-sign-account"><SelectValue placeholder="Pick an account" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="none">— no account yet —</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">When all signers finish, the signed PDF is tagged to this account.</div>
            </div>
          </div>

          <div>
            <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">PDF (max 20 MB) *</Label>
            <Input
              type="file"
              ref={fileInputRef}
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="file:text-blue-700 file:font-medium"
              data-testid="zoho-sign-file"
            />
            {file && <div className="text-[11px] text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Signers *</Label>
              <Button type="button" size="sm" variant="outline" onClick={addRecipient} data-testid="zoho-sign-add-recipient">
                <Plus className="h-3 w-3 mr-1" /> Add signer
              </Button>
            </div>
            <div className="space-y-2">
              {recipients.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <Input placeholder="Full name" value={r.name}  onChange={(e) => updateRecipient(i, "name",  e.target.value)} data-testid={`zoho-sign-recipient-name-${i}`} />
                  <Input placeholder="Email"     value={r.email} onChange={(e) => updateRecipient(i, "email", e.target.value)} type="email" data-testid={`zoho-sign-recipient-email-${i}`} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeRecipient(i)} disabled={recipients.length <= 1} aria-label="Remove signer">
                    <Trash2 className="h-3.5 w-3.5 text-slate-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Expiry (days)</Label>
              <Input type="number" min="1" max="90" value={expireDays} onChange={(e) => setExpireDays(e.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sequential}
                  onChange={(e) => setSequential(e.target.checked)}
                  className="h-4 w-4"
                  data-testid="zoho-sign-sequential"
                />
                <span>Sign one after the other</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-[11px] font-mono text-slate-500">
              {canSend
                ? <span className="text-emerald-700">Ready to send.</span>
                : <span className="text-amber-700">
                    {configured ? "Fill name, upload PDF and add at least one signer." : "Add Zoho Sign credentials first."}
                  </span>}
            </div>
            <Button onClick={send} disabled={!canSend || sending} className="bg-blue-600 hover:bg-blue-700" data-testid="zoho-sign-send">
              <Send className="h-3.5 w-3.5 mr-1" /> {sending ? "Sending…" : "Send for signature"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Envelopes ---------- */}
      <Card className="border-slate-200" data-testid="zoho-sign-envelopes">
        <CardHeader className="pb-3 border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center justify-between">
            <span>Sent work orders</span>
            <span className="text-[11px] font-mono text-slate-500">
              {envelopes.length} total
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingEnvs ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : envelopes.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">
              Nothing sent yet. Upload a PDF above to start the first signature flow.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-500 bg-slate-50/70 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2">Work order</th>
                  <th className="px-4 py-2">Account</th>
                  <th className="px-4 py-2">Signers</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Sent</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {envelopes.map((e) => {
                  const meta = STATUS_META[e.status] || STATUS_META.sent;
                  const Icon = meta.Icon;
                  return (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`envelope-${e.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900">{e.request_name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{e.filename}</div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{e.account_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-700">
                        {(e.recipients || []).map((r) => r.name).join(", ")}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full border ${meta.color}`}>
                          <Icon className="h-3 w-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] font-mono text-slate-500">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {e.status === "signed" && (
                          <Button size="sm" variant="outline" onClick={() => downloadSigned(e)} data-testid={`envelope-download-${e.id}`}>
                            <Download className="h-3.5 w-3.5 mr-1" /> Signed PDF
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
